<template>
    <div v-if="config.display" :style="{'width': config.width}" class="control e-control">
        <div v-if="asField">
            <display-value :id="config.id" :value="value"></display-value>
        </div>
        <div v-else-if="!initialLoad">
            <YouTube
                v-bind="syncfusionConfig"
                :src="value"
                :data-test-id="dataTestId"
                @ready="onReady(); action($event, 'onready')"
                @state-change="stateChange($event);  action($event, 'onstatechange')"
                :ref="ref"
                width="100%"
                height="100%"
                class="youtube-player"
            />
            <p v-if="config.description" class="youtube-custom-message">{{config.description}}</p>
        </div>
    </div>
</template>

<script>
import { controlMixins } from "./controlMixins.js";
// eslint-disable-next-line import/no-extraneous-dependencies
import YouTube from 'vue3-youtube';
import { library } from "@common/library/library";

/*
* Please check following links for get available params and functions of the YouTube player
* https://developers.google.com/youtube/iframe_api_reference
* https://developers.google.com/youtube/player_parameters
* */

export const youtubeComponent = {
    mixins: [controlMixins],
    data: function () {
        return {
            lastPlayTime: 0,
            lastPlayTimeTimeout: false,
            fastForwardDetected: false,
            skippedWarningOpened: false,
            initialLoad: true,
            playTimeInterval: false,
            endTimeout: false,
            videoCompleted: false
        };
    },
    mounted() {
        let tmpElement = document.getElementById('youtube-iframe-js-api-script');
        if (!tmpElement) {
            let ytScript = document.createElement('script');
            ytScript.setAttribute('src', "https://www.youtube.com/iframe_api");
            ytScript.setAttribute('id', 'youtube-iframe-js-api-script');
            ytScript.onload = () => {
                if (typeof YT !== 'undefined' && !YT.loaded) {
                    YT.ready(() => {
                        this.initialLoad = false;
                    });
                } else {
                    this.initialLoad = false;
                }
            };
            document.head.appendChild(ytScript);
        } else {
            this.initialLoad = false;
        }
    },
    computed: {
        ref() {
            return this.config.ref || this.config.id;
        }
    },
    methods: {
        showSkippedWarning() {
            if (this.skippedWarningOpened) return;
            this.skippedWarningOpened = true;
            let player = this.$refs?.[this.ref]?.player;
            if (player) player.pauseVideo();
            library.showDialog(
                "You must watch the entire video. Please do not fast-forward. Please go back to the last place in the video that you watched.",
                () => {
                    this.skippedWarningOpened = false;
                    if (player) {
                        player.seekTo(this.lastPlayTime);
                        player.playVideo();
                    }
                },
                false,
                {
                    title: 'Video Watch Notice',
                    okButtonLabel: 'Go Back',
                    showAsAlert: true
                }
            );
        },
        stateChange(event) {
            let player = this.$refs?.[this.ref]?.player;
            if (typeof YT !== 'undefined' && player) {
                if (this.config && this.config.disableFastForward) {
                    // Update lastPlayTime every 1 seconds
                    if (event.data === YT.PlayerState.PLAYING && !this.playTimeInterval) {
                        this.playTimeInterval = setInterval(() => {
                            let currentPlayTime = player.getCurrentTime();
                            if (currentPlayTime - this.lastPlayTime > 2) { // NOSONAR
                                this.showSkippedWarning();
                            }
                            else {
                                this.lastPlayTime = currentPlayTime;
                                library.setLocalStorage('lastPlayTime' + this.value, this.lastPlayTime);
                            }
                        }, 1000); // NOSONAR
                    }
                }
                if (event.data === YT.PlayerState.ENDED) {
                    // If a user intentionally goes back after watching the video, we don't want to show a dialog warning
                    if (!this.videoCompleted) {
                        this.endTimeout = setTimeout(() => {
                            // watchedEndThreshold is time from end that still counts as "watched"
                            // This allows users to pause or skip the last few seconds video in the last few seconds and still count the video as "watched"
                            let watchedEndThreshold = this?.config?.watchedEndThreshold || 3; // NOSONAR
                            let duration = player.getDuration();
                            if (this.config.disableFastForward && (duration - this.lastPlayTime > watchedEndThreshold)) {
                                this.showSkippedWarning();
                            }
                            else {
                                clearInterval(this.playTimeInterval);
                                this.playTimeInterval = false;
                                library.removeLocalStorage('lastPlayTime' + this.value);
                                this.videoCompleted = true;
                                this.action(event, 'oncomplete');
                            }
                        }, 1100); // NOSONAR
                    }
                }
            }
        },
        onReady() {
            if (
                this.config
                && this.config.allowResume
                && library.getLocalStorage('lastPlayTime' + this.value)
                && this.$refs
                && this.$refs[this.ref]
                && this.$refs[this.ref].player
            ) {
                library.showDialog(
                    "This video has been partially watched. Would you like to resume the video from where you left off?",
                    () => {
                        this.lastPlayTime = library.getLocalStorage('lastPlayTime' + this.value);
                        this.$refs[this.ref].player.seekTo(library.getLocalStorage('lastPlayTime' + this.value));
                    },
                    false,
                    {
                        title: 'Resume Video?',
                        okButtonLabel: 'Resume',
                        cancelButtonLabel: 'Start Over'
                    }
                );
            }
        }
    },
    beforeUnmount() {
        clearInterval(this.playTimeInterval);
        clearTimeout(this.endTimeout);
        this.playTimeInterval = false;
        this.endTimeout = false;
    },
    components: {
        YouTube: YouTube,
    }
};

export default youtubeComponent;
</script>

<style scoped>
:deep(.youtube-player) {
    aspect-ratio: 16/9
}
:deep(.youtube-custom-message) {
    text-align: center;
}
</style>
